import { Component, HostListener, inject, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Store } from '@softline/core';
import { Router } from '@angular/router';
import {
  BackNavigationService,
  BlockingLoadingSpinnerComponent,
  showRequestErrors,
  HeaderComponent,
  SOFTLINE_FEATURE_TITLE,
  TitleStore,
} from '@softline/application';
import { MessageBarStore, ModalStore, SOFTLINE_FEATURE_MESSAGE_BAR, SOFTLINE_FEATURE_MODAL, QuantityInputComponent, ButtonComponent } from '@softline/ui-core';
import { type ProgressStep, ProgressStepsComponent } from '@softline/ui-core/progress';
import { SOFTLINE_FEATURE_INVENTUR } from '../../inventur.shared';
import { InventurStore } from '../../store';
import { BehaviorSubject } from 'rxjs';
import {
  ArtikelMenge,
  Lagerplatz,
  LagerplatzInhalt,
  LagerplatzSucheStore,
  SOFTLINE_FEATURE_LAGERPLATZ_SUCHE,
} from '@softapps/lager/core';
import { unitEntryModalFn } from '@softapps/wws/core';

import { TranslatePipe } from '@softline/ui-core';
import { PageComponent, PageFooterComponent, PageHeaderComponent } from '@softapps/core/pages';

@Component({
  selector: 'soft-mengen-auswahl',
  imports: [
    CommonModule,
    QuantityInputComponent,
    ProgressStepsComponent,
    BlockingLoadingSpinnerComponent,
    HeaderComponent,
    TranslatePipe,
    ButtonComponent,
    PageComponent,
    PageFooterComponent,
    PageHeaderComponent,
  ],
  templateUrl: './mengen-auswahl.page.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./mengen-auswahl.page.css'],
})
export class MengenAuswahlPage implements OnInit {
  private readonly store = inject(Store);
  private readonly router = inject(Router);
  private readonly backNavigationService = inject(BackNavigationService);

  unitEntryModal = unitEntryModalFn();

  readonly steps: ProgressStep[] = [
    {
      header: '#INVENTUR.PAGES.LAGERPLATZ_SCANNEN.TITLE',
      subHeader: '#INVENTUR.PAGES.LAGERPLATZ_SCANNEN.DESCRIPTION',
    },
    {
      header: '#INVENTUR.PAGES.ARTIKEL_AUSWAHL.TITLE',
      subHeader: '#INVENTUR.PAGES.ARTIKEL_AUSWAHL.DESCRIPTION',
    },
    {
      header: '#INVENTUR.PAGES.MENGEN_AUSWAHL.TITLE',
      subHeader: '#INVENTUR.PAGES.MENGEN_AUSWAHL.DESCRIPTION',
    },
  ];

  loading$ = new BehaviorSubject<boolean>(false);
  lagerplatz$ = this.store.observe(
    SOFTLINE_FEATURE_INVENTUR,
    InventurStore.getters.lagerplatz
  );
  artikel = this.store.signal(SOFTLINE_FEATURE_INVENTUR, InventurStore.getters.artikel);

  constructor() {
    //
  }

  ngOnInit(): void {
    this.backNavigationService.set(this);
    this.store.commit(
      SOFTLINE_FEATURE_TITLE,
      TitleStore.mutations.setTitle,
      '#INVENTUR.TITLE'
    );
  }

  ngOnDestroy() {
    this.store.commit(SOFTLINE_FEATURE_TITLE, TitleStore.mutations.setTitle, '');
    this.backNavigationService.set(undefined);
  }

  async navigateBack(): Promise<void> {
    await this.router.navigate(['/inventur/artikel-auswahl']);
  }

  async onMengeChange(menge: number): Promise<void> {
    const artikel = this.store.get(
      SOFTLINE_FEATURE_INVENTUR,
      InventurStore.getters.artikel
    );

    this.store.commit(SOFTLINE_FEATURE_INVENTUR, InventurStore.mutations.setArtikel, {
      ...artikel,
      menge,
      menge_inv: menge,
    });
  }

  @HostListener('window:keydown.enter')
  async onSubmit(): Promise<void> {
    const lagerplatz = this.store.get(
      SOFTLINE_FEATURE_INVENTUR,
      InventurStore.getters.lagerplatz
    );
    const inhalt = this.store.get(
      SOFTLINE_FEATURE_INVENTUR,
      InventurStore.getters.artikel
    );
    this.loading$.next(true);
    try {
      const inventoryResponse = await this.store.dispatch(
        SOFTLINE_FEATURE_INVENTUR,
        InventurStore.actions.inventur,
        {
          lagerplatz: lagerplatz.lagerplatz,
          inhalt: [{ ...inhalt, menge_inv: inhalt.menge }],
        }
      );

      const newInhalt = inventoryResponse?.inhalt[0];

      const index = lagerplatz.inhalt.findIndex(
        (i) =>
          i.artikel.id === newInhalt.artikel.id && i.einheit.id === newInhalt.einheit.id
      );

      if (index > -1) {
        lagerplatz.inhalt[index] = newInhalt;
      }

      this.loading$.next(false);
      this.store.commit(
        SOFTLINE_FEATURE_INVENTUR,
        InventurStore.mutations.setLagerplatz,
        lagerplatz
      );
      await this.store.dispatch(
        SOFTLINE_FEATURE_MESSAGE_BAR,
        MessageBarStore.actions.success,
        '#INVENTUR.MESSAGES.SUCCESS.KORREKTUR'
      );
      console.log('navigateToNext: ', lagerplatz, inhalt);
      await this.navigateToNext(lagerplatz, inhalt);
    } catch (e) {
      this.loading$.next(false);
      showRequestErrors(this.store, e);
    }
  }

  async navigateToNext(
    lagerplatz: LagerplatzInhalt,
    inhalt: ArtikelMenge
  ): Promise<void> {
    if (lagerplatz.inhalt.length > 0) {
      console.log('navigateToNext ->', lagerplatz);
      await this.router.navigate(['/inventur', 'artikel-auswahl']);
      return;
    }

    const lagerplaetze = this.store.get(
      SOFTLINE_FEATURE_LAGERPLATZ_SUCHE,
      LagerplatzSucheStore.getters.data
    );
    const result = await this.store.dispatch(
      SOFTLINE_FEATURE_MODAL,
      ModalStore.actions.choose<Lagerplatz>(),
      {
        title: '#LAGERPLATZ_CORE.DIALOGS.CHOOSE_LAGERPLATZ',
        options: lagerplaetze.map((o) => ({ label: o.lagerplatz, value: o })),
        dismiss: true,
      }
    );
    if (result === 'DISMISSED') {
      await this.router.navigate(['/inventur']);
      return;
    }

    this.loading$.next(true);
    try {
      await this.store.dispatch(
        SOFTLINE_FEATURE_INVENTUR,
        InventurStore.actions.loadLagerplatz,
        result.barcode
      );
      this.loading$.next(false);
      await this.router.navigate(['/inventur/artikel-auswahl']);
    } catch (e) {
      this.loading$.next(false);
      showRequestErrors(this.store, e);
    }
  }

  async openUnitEntryModal(item: ArtikelMenge, idarteheingabe?: number): Promise<void> {
    await this.unitEntryModal({
      params: {
        idartstamm: item.artikel.id,
        idarteheingabe: idarteheingabe,
        idbasisEinheit: item.einheit_lg.id,
        menge: idarteheingabe ? undefined : (item.menge_lg ?? 0),
      },
    });
  }
}
